import React from 'react';
import { FaFileExcel } from 'react-icons/fa';

import api from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/Toast';
import { useConfiguration } from '../../../../../../hooks/Configuration';
import { useLoading } from '../../../../../../hooks/LoadingHook';

import { date } from '../../../../../../utils/date';
import { downloaditPost } from '../../../../../../utils/download';
import { apiUrl } from '../../../../../../config';

/* =========================================================================
 * COLUNAS DO RELATÓRIO
 *
 * • `title`    → vai para o cabeçalho do Excel
 * • `property` → chave do objeto retornado pela API (suporta dot-path,
 *                ex: 'project_id_response.title' para campos aninhados)
 * • `format`   → opcional; transforma o valor antes de gravar
 *
 * Comente com `//` as linhas que NÃO quiser exportar.
 * A ordem das colunas segue a ordem do array.
 * ========================================================================= */
interface Column {
  title: string;
  property: string;
  format?: (value: any, row: Record<string, any>) => string | number;
}

const columns: Column[] = [
  /* ---------- Identificação ---------- */
  { title: 'Hash',            property: 'user_hash' },
  { title: 'Nome completo',   property: 'name' },
  { title: 'Nome social',     property: 'social_name' },
  { title: 'E-mail',          property: 'email' },
  { title: 'Telefone',        property: 'phone' },

  /* ---------- Documento ---------- */
  { title: 'CPF',             property: 'document_number' },
 // { title: 'Tipo de documento', property: 'document_type' },
  { title: 'RG',              property: 'rg' },
//  { title: 'Data de expedição do RG', property: 'rg_date' },
 // { title: 'Órgão Expedidor do RG',   property: 'rg_emissor' },
 // { title: 'UF do RG',        property: 'rg_uf' },
 // { title: 'Título de Eleitor', property: 'voter_registration' },

  /* ---------- Dados pessoais ---------- */
  { title: 'Data de nascimento', property: 'birth_date', format: v => (v ? date(v) : '') },
   { title: 'Gênero',          property: 'gender_id' },
 // { title: 'Etnia',            property: 'etnia' },
   { title: 'Nacionalidade',   property: 'nationality' },
 // { title: 'Naturalidade',    property: 'born_in' },
 // { title: 'Nome da mãe',     property: 'mother_name' },
 // { title: 'Nome do pai',     property: 'father_name' },
  // { title: 'Estado civil',    property: 'marital_status' },
  // { title: 'Tipo sanguíneo',  property: 'blood_id' },
  // { title: 'Deficiência',     property: 'disability_type' },

  /* ---------- Endereço ---------- */
 // { title: 'CEP',             property: 'zipcode' },
 // { title: 'Endereço',        property: 'address' },
 // { title: 'Número',          property: 'address_number' },
 // { title: 'Bairro',          property: 'neighborhood' },
  { title: 'Cidade',          property: 'city' },
  { title: 'Estado',          property: 'state' },
  { title: 'País',            property: 'country' },

  /* ---------- Empresa / Entidade ---------- */
 // { title: 'Empresa',         property: 'company' },
 // { title: 'CNPJ',            property: 'company_document_number' },
 // { title: 'Cargo',           property: 'position' },

  /* ---------- Extras ---------- */
  { title: 'Como conheceu',   property: 'know_about' },
  { title: 'Aceite do termo', property: 'term' },
  // { title: 'Instituição',     property: 'instituicao' },
  // { title: 'CRM',             property: 'crm' },
  // { title: 'Extra 1',         property: 'extra_1' },
  // { title: 'Extra 2',         property: 'extra_2' },
  // { title: 'Extra 3',         property: 'extra_3' },
  // { title: 'Extra 4',         property: 'extra_4' },
  // { title: 'Extra 5',         property: 'extra_5' },

  /* ---------- Relacionamentos (dot-path em campos aninhados) ---------- */
  // { title: 'Projeto',      property: 'project_id_response.title' },
  // { title: 'Evento',       property: 'event_id_response.title' },
  // { title: 'Categoria',    property: 'category_id_response.title' },

  /* ---------- Sistema ---------- */
  { title: 'Data de cadastro', property: 'createdAt', format: v => (v ? date(v) : '') },
 // { title: 'Último login',     property: 'last_login', format: v => (v ? date(v) : '') },
];

/* =========================================================================
 * Helper: lê um valor seguindo um caminho com pontos.
 * Ex: get(obj, 'project_id_response.title')
 * ========================================================================= */
function get(obj: Record<string, any> | undefined, path: string): any {
  if (!obj || !path) return undefined;
  return path.split('.').reduce<any>((acc, key) => acc?.[key], obj);
}

export default function UpdateModule(): JSX.Element {
  const { addToast } = useToast();
  const { addLoading, removeLoading } = useLoading();
  const { configuration } = useConfiguration();

  const prepareDownload = async () => {
    addLoading({ title: 'Gerando relatório de cadastros', key: 'students-report' });

    try {
      const response = await api.get('/students', {
        params: { limitItems: 100000 },
      });

      const students: Array<Record<string, any>> = response?.data?.rows ?? [];

      // Cabeçalho
      const header = columns.map(c => c.title);

      // Linhas
      const rows: Array<Array<string >> = students.map(student =>
        columns.map(col => {
          const raw = get(student, col.property);
          if (col.format) return col.format(raw, student);
          return raw === null || raw === undefined ? '' : raw;
        }),
      );

      // Ordena por nome (coluna "Nome completo", se estiver habilitada)
      const nameIdx = columns.findIndex(c => c.property === 'name');
      if (nameIdx >= 0) {
        rows.sort((a, b) => {
          const na = String(a[nameIdx] ?? '').toLowerCase();
          const nb = String(b[nameIdx] ?? '').toLowerCase();
          return na < nb ? -1 : na > nb ? 1 : 0;
        });
      }

      const fullData = [header, ...rows];

      await downloaditPost({
        url: `${apiUrl}/converter/xlsx/`,
        data: fullData,
        type: 'xlsx',
      });
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao gerar relatório',
        description: err?.response?.data?.message || err?.message || '',
      });
    } finally {
      removeLoading('students-report');
    }
  };

  return (
    <button
      className="searchButton"
      title="Download do relatório de cadastros"
      onClick={prepareDownload}
      type="button"
    >
      <FaFileExcel color="yellow" />
    </button>
  );
}
